import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Button, Checkbox, FormControlLabel, Portal, Typography } from '@material-ui/core';
import { AnimatedItem } from '../animations/AnimatedItem';
import { setFeatureTourOpen } from './featureTourState';

export interface FeatureTourStep {
    // 'left' puts the card beside a target that runs down the right-hand edge of the screen (a side panel), instead of under it
    placement?: 'auto' | 'left';
    // CSS selector (use a data-tour attribute) of what to point at. No target = a centred card.
    // A step whose target is not on the screen is skipped, so a layout change cannot break the tour.
    target?: string;
    title: string;
    badge?: string;
    body: React.ReactNode;
    // a function is read when the card shows, for a tip that depends on the page at that moment
    tip?: string | (() => string | undefined);
    // Runs when the step is about to show - for getting the page ready (for example opening a panel).
    onEnter?: () => void;
}

export interface FeatureTourProps {
    steps: FeatureTourStep[];
    // Shows the "Don't show this again" choice on the first card (for a tour that popped up on its own)
    showDontShowAgain?: boolean;
    // isCompleted: reached the last card and pressed Finish. isDontShowAgain: the user ticked the box.
    onClose: (result: { isCompleted: boolean; isDontShowAgain: boolean }) => void;
}

interface SpotlightRect {
    top: number;
    left: number;
    width: number;
    height: number;
}

interface CardPosition {
    top: number;
    left: number;
    // where the arrow sits: on the card's top or bottom edge (offset from its left), or on its right edge (offset from its top)
    arrowSide: 'top' | 'bottom' | 'right';
    arrowOffset: number;
}

const SPOTLIGHT_PADDING = 6;
const CARD_GAP = 16;
const VIEWPORT_MARGIN = 12;
// a panel that is opening (Collapse) needs this long to settle before the target can be measured
const PAGE_SETTLE_MILLISECONDS = 380;

// The part of the top of the screen a page keeps for itself: the app's fixed header and, on a dashboard, its toolbar that stays in place.
const TOP_INSET = 130;

// Whether the target is cut off: under the fixed header or the sticky toolbar, off the bottom of the screen, or hidden by a scrolling panel it sits
// in (the Quick View's list, a drawer). Scrolling it to the middle brings it into the open - scrolling to "the nearest edge" would leave it behind
// the header. Something that stays in place itself (a sticky toolbar) does not need the header's room, and is left alone when it is in sight.
const isTargetOutOfSight = (targetElement: HTMLElement): boolean => {
    const bounds = targetElement.getBoundingClientRect();
    let isPinned = false;
    let visibleTop = 0;
    let visibleBottom = window.innerHeight - 16;
    for (let element: HTMLElement | null = targetElement.parentElement; element && element !== document.body; element = element.parentElement) {
        const style = window.getComputedStyle(element);
        if (style.position === 'sticky' || style.position === 'fixed') {
            isPinned = true;
        }
        // a panel that scrolls or clips its content only shows what is inside its own box
        if (/(auto|scroll|hidden)/.test(`${style.overflow} ${style.overflowY}`)) {
            const clipBounds = element.getBoundingClientRect();
            visibleTop = Math.max(visibleTop, clipBounds.top);
            visibleBottom = Math.min(visibleBottom, clipBounds.bottom);
        }
    }
    const isSelfPinned = ['sticky', 'fixed'].indexOf(window.getComputedStyle(targetElement).position) !== -1;
    if (!isPinned && !isSelfPinned) {
        visibleTop = Math.max(visibleTop, TOP_INSET);
    }
    return bounds.top < visibleTop || bounds.bottom > visibleBottom;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        blocker: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: theme.zIndex.tooltip + 10,
        },
        spotlight: {
            position: 'fixed',
            zIndex: theme.zIndex.tooltip + 11,
            borderRadius: 10,
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.58)',
            pointerEvents: 'none',
            transition: 'top 0.3s cubic-bezier(.4,0,.2,1), left 0.3s cubic-bezier(.4,0,.2,1), width 0.3s cubic-bezier(.4,0,.2,1), height 0.3s cubic-bezier(.4,0,.2,1)',
            '&::after': {
                content: '""',
                position: 'absolute',
                top: -4,
                left: -4,
                right: -4,
                bottom: -4,
                borderRadius: 12,
                border: '2px solid #fff',
                opacity: 0.9,
                animation: '$ring 1.6s ease-out infinite',
            },
            '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
                '&::after': { animation: 'none' },
            },
        },
        '@keyframes ring': {
            from: { transform: 'scale(0.98)', opacity: 0.9 },
            to: { transform: 'scale(1.08)', opacity: 0 },
        },
        card: {
            position: 'fixed',
            zIndex: theme.zIndex.tooltip + 12,
            width: 340,
            maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
            boxSizing: 'border-box',
            padding: theme.spacing(2, 2.25, 1.75),
            backgroundColor: theme.palette.background.paper,
            borderRadius: 12,
            boxShadow: '0 12px 40px rgba(15, 23, 42, 0.35)',
            transition: 'top 0.3s cubic-bezier(.4,0,.2,1), left 0.3s cubic-bezier(.4,0,.2,1), opacity 0.15s ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'opacity 0.15s ease' },
        },
        cardHidden: { opacity: 0 },
        cardCentered: { width: 420, transform: 'translate(-50%, -50%)' },
        arrow: {
            position: 'absolute',
            width: 14,
            height: 14,
            backgroundColor: theme.palette.background.paper,
            transform: 'rotate(45deg)',
            borderRadius: 3,
        },
        badge: {
            display: 'inline-flex',
            alignItems: 'center',
            padding: theme.spacing(0.25, 1.25),
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: theme.palette.primary.main,
            backgroundColor: 'rgba(81, 115, 255, 0.1)',
        },
        title: { margin: theme.spacing(1, 0, 0.5), fontWeight: 700 },
        body: { color: theme.palette.text.secondary, marginBottom: theme.spacing(1.25) },
        tip: {
            padding: theme.spacing(1, 1.25),
            marginBottom: theme.spacing(1.25),
            borderRadius: 8,
            fontSize: 12.5,
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.action.hover,
        },
        // how far through the tour: a bar rather than a dot per step, which would not fit a long tour in the card
        progressTrack: { height: 3, borderRadius: 2, marginBottom: theme.spacing(1.25), backgroundColor: theme.palette.divider, overflow: 'hidden' },
        progressFill: { height: '100%', borderRadius: 2, backgroundColor: theme.palette.primary.main, transition: 'width 0.3s cubic-bezier(.4,0,.2,1)', '@media (prefers-reduced-motion: reduce)': { transition: 'none' } },
        footer: { display: 'flex', alignItems: 'center', gap: theme.spacing(0.5), minHeight: 36 },
        footerSpacer: { flex: 1, minWidth: 0 },
        quietButton: { color: theme.palette.text.secondary, textTransform: 'none' },
        dontShowAgain: { marginLeft: theme.spacing(-0.75), marginRight: 0 },
        dontShowAgainLabel: { fontSize: 12.5, color: theme.palette.text.secondary },
    })
);

// Sizes the dimmed page, the spotlight and the card around whatever the current step points at.
export const FeatureTour: React.FC<FeatureTourProps> = ({ steps, showDontShowAgain = false, onClose }) => {
    const classes = useStyles();
    const [stepIndex, setStepIndex] = useState(0);
    const [spotlightRect, setSpotlightRect] = useState<SpotlightRect | null>(null);
    const [cardPosition, setCardPosition] = useState<CardPosition | null>(null);
    const [isStepReady, setIsStepReady] = useState(false);
    const [isDontShowAgain, setIsDontShowAgain] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);
    const nextButtonRef = useRef<HTMLButtonElement>(null);
    // which way the user is moving, so a step with a missing target is skipped in the same direction
    const directionRef = useRef<1 | -1>(1);
    const hasClosedRef = useRef(false);
    const previouslyFocusedRef = useRef<HTMLElement | null>(document.activeElement as HTMLElement | null);

    useEffect(() => {
        setFeatureTourOpen(true);
        return () => setFeatureTourOpen(false);
    }, []);

    const step = steps[stepIndex];
    const isFirstStep = stepIndex === 0;
    const isLastStep = stepIndex === steps.length - 1;

    const closeTour = useCallback((isCompleted: boolean) => {
        if (hasClosedRef.current) {
            return;
        }
        hasClosedRef.current = true;
        onClose({ isCompleted, isDontShowAgain });
    }, [onClose, isDontShowAgain]);

    const goToNextStep = useCallback(() => {
        directionRef.current = 1;
        if (isLastStep) {
            closeTour(true);
        } else {
            setIsStepReady(false);
            setStepIndex((index) => index + 1);
        }
    }, [isLastStep, closeTour]);

    const goToPreviousStep = useCallback(() => {
        directionRef.current = -1;
        if (!isFirstStep) {
            setIsStepReady(false);
            setStepIndex((index) => index - 1);
        }
    }, [isFirstStep]);

    const measureTarget = useCallback((): SpotlightRect | null => {
        const targetElement = step.target ? document.querySelector(step.target) as HTMLElement | null : null;
        // a collapsed panel keeps its children in the page but hidden - there is nothing to point at
        if (!targetElement || window.getComputedStyle(targetElement).visibility === 'hidden') {
            return null;
        }
        const targetBounds = targetElement.getBoundingClientRect();
        if (targetBounds.width === 0 && targetBounds.height === 0) {
            return null;
        }
        return {
            top: targetBounds.top - SPOTLIGHT_PADDING,
            left: targetBounds.left - SPOTLIGHT_PADDING,
            width: targetBounds.width + SPOTLIGHT_PADDING * 2,
            height: targetBounds.height + SPOTLIGHT_PADDING * 2,
        };
    }, [step]);

    // Gets the page ready for the step, waits for it to settle, then shows the spotlight - or skips a step that has nothing to point at.
    useEffect(() => {
        let isCancelled = false;
        if (step.onEnter) {
            step.onEnter();
        }
        const showStep = () => {
            if (isCancelled) {
                return;
            }
            if (!step.target) {
                setSpotlightRect(null);
                setIsStepReady(true);
                return;
            }
            const targetElement = document.querySelector(step.target);
            if (targetElement && targetElement.scrollIntoView && isTargetOutOfSight(targetElement as HTMLElement)) {
                targetElement.scrollIntoView({ block: 'center' });
            }
            const measuredRect = measureTarget();
            if (!measuredRect) {
                // nothing to point at on this screen: move on the way the user was going (or stop if there is nowhere to go)
                const nextIndex = stepIndex + directionRef.current;
                if (nextIndex < 0 || nextIndex >= steps.length) {
                    closeTour(directionRef.current === 1);
                } else {
                    setStepIndex(nextIndex);
                }
                return;
            }
            setSpotlightRect(measuredRect);
            setIsStepReady(true);
        };
        const settleTimer = window.setTimeout(showStep, step.onEnter ? PAGE_SETTLE_MILLISECONDS : 0);
        return () => {
            isCancelled = true;
            window.clearTimeout(settleTimer);
        };
        // only a change of step restarts this: the callbacks and step it reads are the current ones for that step
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepIndex]);

    // Keeps the spotlight on its target when the window is resized or the page scrolls.
    useEffect(() => {
        if (!step.target || !isStepReady) {
            return;
        }
        let frameId = 0;
        const refreshSpotlight = () => {
            window.cancelAnimationFrame(frameId);
            frameId = window.requestAnimationFrame(() => {
                const measuredRect = measureTarget();
                if (measuredRect) {
                    setSpotlightRect(measuredRect);
                }
            });
        };
        window.addEventListener('resize', refreshSpotlight);
        window.addEventListener('scroll', refreshSpotlight, true);
        // the target growing or shrinking by itself (a card whose data arrives after the step shows)
        const targetElement = document.querySelector(step.target);
        const resizeObserver = typeof ResizeObserver !== 'undefined' && targetElement ? new ResizeObserver(refreshSpotlight) : null;
        if (resizeObserver && targetElement) {
            resizeObserver.observe(targetElement);
        }
        return () => {
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            window.cancelAnimationFrame(frameId);
            window.removeEventListener('resize', refreshSpotlight);
            window.removeEventListener('scroll', refreshSpotlight, true);
        };
    }, [step, isStepReady, measureTarget]);

    // Puts the card under its target (or above it when there is no room), with the arrow pointing at the target's middle.
    useLayoutEffect(() => {
        const cardElement = cardRef.current;
        if (!cardElement || !isStepReady) {
            return;
        }
        if (!spotlightRect) {
            setCardPosition({ top: window.innerHeight / 2, left: window.innerWidth / 2, arrowSide: 'top', arrowOffset: 0 });
            return;
        }
        const cardWidth = cardElement.offsetWidth;
        const cardHeight = cardElement.offsetHeight;
        const cardLeftOfTarget = spotlightRect.left - cardWidth - CARD_GAP;
        if (step.placement === 'left' && cardLeftOfTarget >= VIEWPORT_MARGIN) {
            // centred on the part of the target that is on screen, so a tall panel does not push the card out of view
            const visibleTop = Math.max(spotlightRect.top, VIEWPORT_MARGIN);
            const visibleBottom = Math.min(spotlightRect.top + spotlightRect.height, window.innerHeight - VIEWPORT_MARGIN);
            const targetMiddle = (visibleTop + visibleBottom) / 2;
            const top = Math.min(Math.max(VIEWPORT_MARGIN, targetMiddle - cardHeight / 2), window.innerHeight - cardHeight - VIEWPORT_MARGIN);
            setCardPosition({
                top,
                left: cardLeftOfTarget,
                arrowSide: 'right',
                arrowOffset: Math.min(Math.max(16, targetMiddle - top - 7), cardHeight - 30),
            });
            return;
        }
        const targetMiddle = spotlightRect.left + spotlightRect.width / 2;
        const left = Math.min(Math.max(VIEWPORT_MARGIN, targetMiddle - cardWidth / 2), window.innerWidth - cardWidth - VIEWPORT_MARGIN);
        const hasRoomBelow = spotlightRect.top + spotlightRect.height + CARD_GAP + cardHeight <= window.innerHeight - VIEWPORT_MARGIN;
        setCardPosition({
            top: hasRoomBelow ? spotlightRect.top + spotlightRect.height + CARD_GAP : Math.max(VIEWPORT_MARGIN, spotlightRect.top - cardHeight - CARD_GAP),
            left,
            arrowSide: hasRoomBelow ? 'top' : 'bottom',
            arrowOffset: Math.min(Math.max(16, targetMiddle - left - 7), cardWidth - 30),
        });
    }, [spotlightRect, isStepReady, stepIndex, step.placement]);

    // Keyboard: the primary button is focused so Enter works, and the arrow keys / Escape do what people expect.
    useEffect(() => {
        if (isStepReady && nextButtonRef.current) {
            nextButtonRef.current.focus();
        }
    }, [isStepReady, stepIndex]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeTour(false);
            } else if (event.key === 'ArrowRight') {
                goToNextStep();
            } else if (event.key === 'ArrowLeft') {
                goToPreviousStep();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [closeTour, goToNextStep, goToPreviousStep]);

    // give focus back to whatever had it (the help button, for example) when the tour goes away
    useEffect(() => () => {
        if (previouslyFocusedRef.current && previouslyFocusedRef.current.focus) {
            previouslyFocusedRef.current.focus();
        }
    }, []);

    const tipText = typeof step.tip === 'function' ? step.tip() : step.tip;
    const isCentered = !step.target;
    const cardStyle: React.CSSProperties = cardPosition ? { top: cardPosition.top, left: cardPosition.left } : { top: -9999, left: -9999 };
    const spotlightStyle: React.CSSProperties = spotlightRect
        ? { top: spotlightRect.top, left: spotlightRect.left, width: spotlightRect.width, height: spotlightRect.height }
        : { top: window.innerHeight / 2, left: window.innerWidth / 2, width: 0, height: 0 };
    const cardClassName = [classes.card, isCentered ? classes.cardCentered : '', isStepReady && cardPosition ? '' : classes.cardHidden].join(' ');

    return (
        <Portal>
            <div className={classes.blocker} />
            <div className={classes.spotlight} style={spotlightStyle} />
            <div
                ref={cardRef}
                className={cardClassName}
                style={cardStyle}
                role="dialog"
                aria-modal="true"
                aria-labelledby="feature-tour-title"
            >
                {!isCentered && cardPosition && (
                    <div
                        className={classes.arrow}
                        style={cardPosition.arrowSide === 'right'
                            ? { top: cardPosition.arrowOffset, right: -7 }
                            : { left: cardPosition.arrowOffset, top: cardPosition.arrowSide === 'top' ? -7 : undefined, bottom: cardPosition.arrowSide === 'bottom' ? -7 : undefined }}
                    />
                )}
                <AnimatedItem noFill={true} key={stepIndex}>
                    {step.badge && <span className={classes.badge}>{step.badge}</span>}
                    <Typography variant="h6" component="h3" id="feature-tour-title" className={classes.title}>{step.title}</Typography>
                    <Typography variant="body2" component="div" className={classes.body}>{step.body}</Typography>
                    {tipText && <div className={classes.tip}>{tipText}</div>}
                </AnimatedItem>
                {isFirstStep && showDontShowAgain && (
                    <FormControlLabel
                        className={classes.dontShowAgain}
                        control={<Checkbox size="small" color="primary" checked={isDontShowAgain} onChange={(event) => setIsDontShowAgain(event.target.checked)} />}
                        label={<span className={classes.dontShowAgainLabel}>Don't show this again</span>}
                    />
                )}
                <div
                    className={classes.progressTrack}
                    role="progressbar"
                    aria-valuemin={1}
                    aria-valuemax={steps.length}
                    aria-valuenow={stepIndex + 1}
                    aria-label={`Step ${stepIndex + 1} of ${steps.length}`}
                >
                    <div className={classes.progressFill} style={{ width: `${((stepIndex + 1) / steps.length) * 100}%` }} />
                </div>
                <div className={classes.footer}>
                    {!isLastStep && <Button size="small" className={classes.quietButton} onClick={() => closeTour(false)}>Skip</Button>}
                    <div className={classes.footerSpacer} />
                    {!isFirstStep && <Button size="small" className={classes.quietButton} onClick={goToPreviousStep}>Back</Button>}
                    <Button
                        ref={nextButtonRef}
                        size="small"
                        variant="contained"
                        color="primary"
                        disableElevation={true}
                        onClick={goToNextStep}
                    >
                        {isLastStep ? 'Finish' : isFirstStep ? 'Show me' : 'Next'}
                    </Button>
                </div>
            </div>
        </Portal>
    );
};
